import React, { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import userService from '../../services/userService';
import usePurchaseOrder from '../../hooks/usePurchaseOrder';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import validateUtils from '../../utils/validate';
import SearchableSelect from '../common/SearchableSelect';
import purchaseOrderService from '../../services/purchaseOrderService';

const AssignSupervisorModal = ({ isOpen, adviceId, onClose, onAssigned }) => {
    const { t } = useTranslation();
    const { assignSupervisorToAdvice } = usePurchaseOrder();
    const loadingRef = useRef(false);
    const adviceLoadingRef = useRef(false);

    const [supervisors, setSupervisors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [selectedSupervisor, setSelectedSupervisor] = useState('');
    const [driverContact, setDriverContact] = useState('');
    const [vehicleRemark, setVehicleRemark] = useState('');
    const [internalRemark, setInternalRemark] = useState('');
    const [adviceLoading, setAdviceLoading] = useState(false);
    const [adviceData, setAdviceData] = useState(null);

    // Reset form and fetch data when modal opens
    useEffect(() => {
        if (isOpen && adviceId) {
            setSelectedSupervisor('');
            setDriverContact('');
            setVehicleRemark('');
            setInternalRemark('');
            setError(null);
            setAdviceData(null);

            const fetchAdviceDetails = async () => {
                if (adviceLoadingRef.current) return;
                adviceLoadingRef.current = true;
                setAdviceLoading(true);
                try {
                    const res = await purchaseOrderService.getPurchaseAdviceById(adviceId);
                    if (res.success && res.data) {
                        const data = res.data;
                        setAdviceData(data);
                        setDriverContact(data.driver_contact || '');
                        setVehicleRemark(data.vehicle_remark || '');
                        setInternalRemark(data.internal_remark || '');

                        if (data.delivery_supervisor_id) {
                            setSelectedSupervisor(String(data.delivery_supervisor_id));
                        }
                    }
                } catch (err) {
                    console.error("Failed to fetch advice details:", err);
                } finally {
                    setAdviceLoading(false);
                    adviceLoadingRef.current = false;
                }
            };

            fetchAdviceDetails();
        } else {
            adviceLoadingRef.current = false;
        }
    }, [isOpen, adviceId]);

    // Load supervisors when modal opens
    useEffect(() => {
        if (!isOpen) return;

        const processUsers = (users) => {
            const normalizedUsers = (users || []).map(u => {
                const name = u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                const isActive = !!u.is_active_now;
                const pendingCount = u.pending_orders_count || 0;

                return {
                    ...u,
                    value: String(u.id), // SearchableSelect expects 'value'
                    label: name, // SearchableSelect expects 'label'
                    richLabel: (
                        <div className="flex items-center justify-between w-full">
                            <span>{name}</span>
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold">
                                <span style={{ color: isActive ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR }}>
                                    ({isActive ? 'Available' : 'Not Available'})
                                </span>
                                <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                    - {pendingCount} Pending
                                </span>
                            </div>
                        </div>
                    ),
                    style: {
                        backgroundColor: isActive
                            ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.2)
                            : colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.2)
                    }
                };
            });

            setSupervisors(normalizedUsers);
        };

        const loadData = async () => {
            if (loadingRef.current) return;

            loadingRef.current = true;
            setLoading(true);
            setError(null);
            try {
                const res = await userService.getDeliverySupervisorsStatus();
                if (res.success) {
                    const all = Array.isArray(res.data) ? res.data : [];
                    processUsers(all);
                } else {
                    processUsers([]);
                }
            } catch (err) {
                setError(t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.messages.loadingUsers'));
            } finally {
                setLoading(false);
                loadingRef.current = false;
            }
        };

        loadData();
    }, [isOpen, t]);

    // Handle supervisor name matching if ID was missing from API
    useEffect(() => {
        if (isOpen && supervisors.length > 0 && !selectedSupervisor && adviceData) {
            // Check if we can find supervisor by name (fallback if ID wasn't provided directly)
            if (adviceData.supervisor_name && !adviceData.delivery_supervisor_id) {
                const match = supervisors.find(s =>
                    s.label.toLowerCase() === adviceData.supervisor_name.toLowerCase()
                );
                if (match) {
                    setSelectedSupervisor(match.value);
                }
            }
        }
    }, [isOpen, supervisors, selectedSupervisor, adviceData]);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        setError(null);
        if (!adviceId) return setError(t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.messages.invalidAdvice'));

        const supId = selectedSupervisor ? Number(selectedSupervisor) : null;

        if (!supId) return setError(t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.messages.selectSupervisor'));

        if (!driverContact || String(driverContact).trim() === '') {
            return setError(t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.messages.driverContactRequired'));
        } else if (!validateUtils.isValidPhoneNumber(driverContact)) {
            return setError(t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.messages.invalidDriverContact'));
        }

        setSubmitting(true);
        try {
            const payload = {
                delivery_supervisor_id: supId,
                driver_contact: String(driverContact).trim(),
                vehicle_remark: String(vehicleRemark).trim() || '',
                internal_remark: internalRemark || '',
            };

            const result = await assignSupervisorToAdvice(adviceId, payload);
            if (result && result.success) {
                if (onAssigned) onAssigned(result.data || {});
                onClose && onClose();
            } else {
                setError(result?.error || t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.messages.assignFailed'));
            }
        } catch (err) {
            setError(err?.message || t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.messages.assignFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl relative" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <div>
                        <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.title')}</h2>
                        <p className="mt-1 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.subtitle')}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6">
                    {loading || adviceLoading ? (
                        <div className="flex flex-col items-center justify-center py-10">
                            <div className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}></div>
                            <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                {loading ? t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.messages.loadingUsers') : 'Loading advice details...'}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.labels.supervisor')} <span>*</span></label>
                                    <SearchableSelect
                                        value={selectedSupervisor}
                                        onChange={(e) => setSelectedSupervisor(e.target.value)}
                                        options={supervisors}
                                        placeholder={t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.placeholders.supervisor')}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.labels.driverContact')} <span>*</span></label>
                                    <input
                                        value={driverContact}
                                        onChange={(e) => setDriverContact(e.target.value)}
                                        className="w-full px-4 py-3 rounded-xl border-2"
                                        placeholder={t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.placeholders.driverContact')}
                                        style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                        maxLength={10}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.labels.vehicleRemark')}</label>
                                    <input
                                        value={vehicleRemark}
                                        onChange={(e) => setVehicleRemark(e.target.value)}
                                        className="w-full px-4 py-3 rounded-xl border-2"
                                        placeholder={t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.placeholders.vehicleRemark')}
                                        style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>Internal Remarks</label>
                                <textarea
                                    value={internalRemark}
                                    onChange={(e) => setInternalRemark(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border-2 resize-none text-sm"
                                    rows="2"
                                    placeholder="Internal remarks (optional)"
                                    style={{ backgroundColor: '#f0fdf4', borderColor: '#86efac' }}
                                />
                            </div>

                            {error && (
                                <div className="p-4 rounded-xl" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1), color: UI_COLORS.STATUS.ERROR }}>
                                    <p className="text-sm font-medium">{error}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-4 p-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <button
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl font-medium transition-colors"
                        disabled={submitting}
                        style={{
                            backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background,
                            color: UI_COLORS.BUTTONS.SECONDARY.text,
                            opacity: submitting ? 0.6 : 1,
                        }}
                    >
                        {t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.buttons.cancel')}
                    </button>

                    <button
                        onClick={handleSubmit}
                        className="px-6 py-3 rounded-xl font-semibold shadow-lg transition-all hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
                        disabled={submitting || loading}
                        style={{
                            backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                            color: UI_COLORS.BUTTONS.PRIMARY.text,
                        }}
                    >
                        <span className="inline-flex items-center gap-2">
                            {submitting && (
                                <span className="animate-spin">
                                    <div
                                        className="h-4 w-4 rounded-full border-2 border-b-transparent"
                                        style={{
                                            borderColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.text, 0.6),
                                        }}
                                    />
                                </span>
                            )}
                            {!submitting && <FontAwesomeIcon icon={faSave} />}
                            <span>
                                {submitting
                                    ? t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.buttons.assigning')
                                    : t('purchaseOrdersModule.purchaseOrders.forms.assignSupervisor.buttons.assign')}
                            </span>
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignSupervisorModal;
